import { createContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import type { FC, PropsWithChildren } from 'react';

import type { EventTreeValue } from 'app/pages/EventsPage/types';

import { initialState, reducer } from './reducer';
import type { MonitoringFilterContextValue, MonitoringFilter } from './types';

const defaultValue: MonitoringFilterContextValue = {
  ...initialState,
  setFilter: () => undefined,
  resetFilters: () => undefined,
  setSelectedTournaments: () => undefined
};

const MonitoringFilterContext = createContext(defaultValue);

// Mutable value to keep state after page change
// Give the possibility to reset filter with last state when user come back on the page
let lastEventState = initialState;

const MonitoringFilterProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, lastEventState);

  useEffect(() => {
    lastEventState = state;
  }, [state]);

  const setFilter = useCallback(
    (key: keyof MonitoringFilter, value: any) => {
      const { filters } = state;

      dispatch({
        type: 'UPDATE_FILTER',
        payload: { ...filters, [key]: value }
      });
    },
    [dispatch, state]
  );

  const resetFilters = useCallback(() => {
    const { filters } = state;

    dispatch({
      type: 'UPDATE_FILTER',
      payload: {
        ...filters,
        markets: initialState.filters.markets,
        scoreThreshold: initialState.filters.scoreThreshold
      }
    });
  }, [dispatch, state]);

  const setSelectedTournaments = useCallback(
    (value: EventTreeValue) => {
      const { filters } = state;

      dispatch({
        type: 'UPDATE_FILTER',
        payload: { ...filters, selectedTournaments: value }
      });
    },
    [dispatch, state]
  );

  const value = useMemo(
    () => ({
      ...state,
      setFilter,
      resetFilters,
      setSelectedTournaments
    }),
    [state, setFilter, resetFilters]
  );

  return (
    <MonitoringFilterContext.Provider value={value}>{children}</MonitoringFilterContext.Provider>
  );
};

export default MonitoringFilterContext;
export { MonitoringFilterProvider };
